/**
 * Early Toast Notification System
 * 
 * A lightweight, framework-independent toast notification system that works
 * during app initialization before PrimeVue ToastService is available.
 * 
 * This is specifically designed for critical errors that occur during the
 * authentication and initialization phase.
 */

interface EarlyToastOptions {
    message: string;
    type?: 'error' | 'warning' | 'info' | 'success';
    duration?: number;
}

/**
 * Show a toast notification using vanilla JavaScript
 * Works even before Vue/PrimeVue are fully initialized
 */
export function showEarlyToast(options: EarlyToastOptions): void {
    const {
        message,
        type = 'error',
        duration = 0 // 0 means it stays until manually closed
    } = options;

    // Check if a toast container already exists
    let container = document.getElementById('early-toast-container');
    
    if (!container) {
        // Create container if it doesn't exist
        container = document.createElement('div');
        container.id = 'early-toast-container';
        container.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 9999;
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-width: 400px;
        `;
        document.body.appendChild(container);
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'early-toast';
    
    // Set colors based on type
    const colors = {
        error: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
        warning: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
        info: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
        success: { bg: '#d1fae5', border: '#10b981', text: '#065f46' }
    };
    
    const color = colors[type];
    
    toast.style.cssText = `
        background-color: ${color.bg};
        border-left: 4px solid ${color.border};
        color: ${color.text};
        padding: 16px;
        border-radius: 6px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        display: flex;
        align-items: flex-start;
        gap: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        animation: slideIn 0.3s ease-out;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('early-toast-styles')) {
        const style = document.createElement('style');
        style.id = 'early-toast-styles';
        style.textContent = `
            @keyframes slideIn {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            @keyframes slideOut {
                from {
                    transform: translateX(0);
                    opacity: 1;
                }
                to {
                    transform: translateX(100%);
                    opacity: 0;
                }
            }
        `;
        document.head.appendChild(style);
    }

    // Create icon based on type
    const icon = document.createElement('span');
    icon.style.cssText = `
        flex-shrink: 0;
        font-size: 20px;
    `;
    
    const icons = {
        error: '❌',
        warning: '⚠️',
        info: 'ℹ️',
        success: '✅'
    };
    icon.textContent = icons[type];

    // Create message container
    const messageContainer = document.createElement('div');
    messageContainer.style.cssText = `
        flex: 1;
        word-wrap: break-word;
    `;
    messageContainer.textContent = message;

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.style.cssText = `
        background: none;
        border: none;
        color: ${color.text};
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        margin: -4px -4px 0 0;
        opacity: 0.6;
        transition: opacity 0.2s;
        flex-shrink: 0;
    `;
    closeButton.onmouseover = () => closeButton.style.opacity = '1';
    closeButton.onmouseout = () => closeButton.style.opacity = '0.6';
    closeButton.onclick = () => removeToast(toast);

    // Assemble toast
    toast.appendChild(icon);
    toast.appendChild(messageContainer);
    toast.appendChild(closeButton);
    container.appendChild(toast);

    // Auto-remove after duration (if specified)
    if (duration > 0) {
        setTimeout(() => removeToast(toast), duration);
    }
}

/**
 * Remove a toast with animation
 */
function removeToast(toast: HTMLElement): void {
    toast.style.animation = 'slideOut 0.3s ease-in';
    setTimeout(() => {
        toast.remove();
        
        // Remove container if no more toasts
        const container = document.getElementById('early-toast-container');
        if (container && container.children.length === 0) {
            container.remove();
        }
    }, 300);
}

// Track if authentication error is already shown to prevent duplicates
let authErrorShown = false;

/**
 * Show a critical authentication error
 * This is specifically for JWT token failures during initialization
 * Only shows one error at a time, even if called multiple times
 */
export function showAuthenticationError(): void {
    // Prevent duplicate authentication errors
    if (authErrorShown) {
        console.warn('Authentication error already shown, skipping duplicate');
        return;
    }
    
    authErrorShown = true;
    
    showEarlyToast({
        message: 'Your session has expired. Please log in again.',
        type: 'error',
        duration: 4000 // Auto-hide after 4 seconds
    });
    
    // Reset flag after 5 seconds to allow showing again if needed
    setTimeout(() => {
        authErrorShown = false;
    }, 5000);
}

/**
 * Clear all early toast notifications
 */
export function clearEarlyToasts(): void {
    const container = document.getElementById('early-toast-container');
    if (container) {
        container.remove();
    }
}

/**
 * Reset the authentication error flag (for testing purposes)
 * @internal
 */
export function resetAuthErrorFlag(): void {
    authErrorShown = false;
}

